import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@material-ui/core";
import BathtubIcon from "@mui/icons-material/Bathtub";
import BedIcon from "@mui/icons-material/Bed";
import CloseIcon from "@mui/icons-material/Close";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import InputText from "components/_refactor/text/input";
import Api from "lib/api/httpRequestHelper";

import MonitoramentoPrecoCadastroSelect from "./MonitoramentoPrecoCadastroSelect";
import { MP } from "./mpConstants";
import {
  initialConcorrenteRow,
  MARCA_GENERICA_NOME,
  mapProdutoConcorrenteCadastroFromApi,
  mergeOpcoesCadastro,
  normalizeCategoriaKey,
  resolveMarcaGenericaId,
  unwrapApiResult,
} from "./mpMappers";

const LINHA_MAX = 100;

function resolveCategoriaInicial(produto) {
  return normalizeCategoriaKey(produto?.categoria);
}

function CategoriaCard({
  selected,
  title,
  icon: Icon,
  onSelect,
  disabled = false,
  classes,
}) {
  const rootClass = [
    classes.tipoCard,
    selected ? classes.tipoCardSelected : "",
    disabled ? classes.tipoCardDisabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      className={rootClass}
      onClick={() => !disabled && onSelect()}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Icon style={{ fontSize: 36, color: MP.primary, marginBottom: 6 }} />
        <div className={classes.tipoCardTitle}>{title}</div>
      </div>
    </div>
  );
}

CategoriaCard.propTypes = {
  selected: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  classes: PropTypes.object.isRequired,
};

function validateForm({ categoria, quantidadeFios, gramatura, linha, opcoes }) {
  const errors = {};
  const linhaTrim = String(linha ?? "").trim();

  if (!categoria) {
    errors.categoria = "Selecione a categoria do produto.";
  } else if (categoria === "cama") {
    if (!String(quantidadeFios ?? "").trim()) {
      errors.quantidadeFios = "Selecione a quantidade de fios.";
    } else if (!opcoes.tecidoBases.includes(String(quantidadeFios).trim())) {
      errors.quantidadeFios =
        "Selecione uma quantidade de fios válida da lista.";
    }
  } else if (!String(gramatura ?? "").trim()) {
    errors.gramatura = "Selecione a gramatura.";
  } else if (!opcoes.gramaturas.includes(String(gramatura).trim())) {
    errors.gramatura = "Selecione uma gramatura válida da lista.";
  }

  if (!linhaTrim) {
    errors.linha = "Informe a linha concorrente.";
  } else if (linhaTrim.length > LINHA_MAX) {
    errors.linha = `A linha concorrente deve ter no máximo ${LINHA_MAX} caracteres.`;
  }

  return errors;
}

export default function MonitoramentoPrecoCadastroConcorrenteDialog({
  open,
  onClose,
  produto,
  marcasConcorrentes,
  opcoesCadastroPorCategoria,
  classes,
  onSaved,
}) {
  const snackbar = useSnackbar();
  const categoria = useMemo(
    () => resolveCategoriaInicial(produto),
    [produto?.id, produto?.categoria],
  );
  const [marcaId, setMarcaId] = useState("");
  const [linha, setLinha] = useState("");
  const [quantidadeFios, setQuantidadeFios] = useState("");
  const [gramatura, setGramatura] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const marcaGenericaId = useMemo(
    () => resolveMarcaGenericaId(marcasConcorrentes),
    [marcasConcorrentes],
  );

  const marcaOptions = useMemo(
    () =>
      (marcasConcorrentes || []).map((m) => ({
        value: String(m.idConcorrenteCatalog),
        label: m.nome,
      })),
    [marcasConcorrentes],
  );

  const opcoes = useMemo(() => {
    if (!categoria) {
      return { nomesComerciais: [], gramaturas: [], tecidoBases: [] };
    }
    const doCatalogo = opcoesCadastroPorCategoria?.[categoria] ?? {
      nomesComerciais: [],
      gramaturas: [],
      tecidoBases: [],
    };
    const doProduto =
      produto && categoria === "cama"
        ? { tecidoBases: [produto.tecidoBase], gramaturas: [] }
        : produto && categoria === "banho"
          ? { gramaturas: [produto.gramatura], tecidoBases: [] }
          : null;
    return mergeOpcoesCadastro(doCatalogo, doProduto);
  }, [opcoesCadastroPorCategoria, categoria, produto]);

  const quantidadeFiosOptions = useMemo(
    () =>
      opcoes.tecidoBases.map((v) => ({
        value: v,
        label: v,
      })),
    [opcoes.tecidoBases],
  );

  const gramaturaOptions = useMemo(
    () =>
      opcoes.gramaturas.map((g) => ({
        value: g,
        label: g,
      })),
    [opcoes.gramaturas],
  );

  const listasVazias = useMemo(() => {
    if (!categoria) return true;
    if (categoria === "cama") return opcoes.tecidoBases.length === 0;
    return opcoes.gramaturas.length === 0;
  }, [categoria, opcoes]);

  useEffect(() => {
    if (!open) return;
    const genericaValue =
      marcaGenericaId != null ? String(marcaGenericaId) : "";
    setMarcaId(genericaValue);
    setLinha("");
    setQuantidadeFios("");
    setGramatura("");
    setErrors({});
    setSubmitted(false);
  }, [open, produto?.id, marcaGenericaId]);

  const handleSalvar = async () => {
    setSubmitted(true);
    const validationErrors = validateForm({
      categoria,
      quantidadeFios,
      gramatura,
      linha,
      opcoes,
    });
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      snackbar.showMessage(
        "Verifique os campos destacados antes de salvar.",
        { severity: "warning" },
      );
      return;
    }

    if (!produto?.id) {
      snackbar.showMessage(
        "Selecione um produto Karsten antes de cadastrar concorrentes.",
        { severity: "warning" },
      );
      return;
    }

    if (listasVazias) {
      snackbar.showMessage(
        "Não há opções cadastradas para esta categoria. Entre em contato com o suporte.",
        { severity: "warning" },
      );
      return;
    }

    const idConcorrenteCatalog = marcaId ? Number(marcaId) : null;

    setSalvando(true);
    try {
      const payload = {
        idProdutoReferencia: produto.id,
        categoria,
        linha: String(linha).trim(),
        tecidoBase:
          categoria === "cama" ? String(quantidadeFios).trim() : "",
        gramatura: categoria === "banho" ? String(gramatura).trim() : "",
        idConcorrenteCatalog,
      };

      const res = await Api.post(
        "produtos-concorrentes",
        "priceMonitoring",
        payload,
        true,
      );
      const raw = unwrapApiResult(res);
      const mapped = mapProdutoConcorrenteCadastroFromApi(raw);
      const row = initialConcorrenteRow(mapped.concorrente, { encontrado: true });
      onSaved(row, mapped.mensagem);
      onClose();
    } catch (e) {
      snackbar.showMessage(
        e?.message || "Não foi possível cadastrar o produto concorrente.",
        { severity: "error" },
      );
    } finally {
      setSalvando(false);
    }
  };

  const linhaHelper = `${String(linha).length}/${LINHA_MAX}`;
  const linhaPlaceholder =
    categoria === "banho"
      ? "Ex.: Unique, Premium, Soft..."
      : "Ex.: Unique, Royal, Supreme...";

  const renderSelectField = ({
    label,
    value,
    onChange,
    options,
    errorKey,
    required = true,
    emptyOptionLabel = "Selecione",
  }) => {
    const semOpcoes = options.length === 0;
    const validationMsg = submitted ? errors[errorKey] : "";
    const emptyMsg = semOpcoes
      ? `Não há opções de ${label.toLowerCase()} nos produtos Karsten desta categoria.`
      : "";

    return (
      <MonitoramentoPrecoCadastroSelect
        label={label}
        value={value ?? ""}
        onChange={onChange}
        options={options}
        required={required}
        disabled={required && semOpcoes}
        error={submitted && Boolean(validationMsg)}
        helperText={validationMsg || undefined}
        emptyHint={semOpcoes ? emptyMsg : ""}
        emptyOptionLabel={emptyOptionLabel}
        classes={classes}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={salvando ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      classes={{ paper: classes.cadastroConcorrenteDialogPaper }}
    >
      <DialogTitle disableTypography className={classes.minhasColetasDialogTitle}>
        <Typography variant="h6" style={{ fontWeight: 700, color: MP.primary }}>
          Cadastro de Produto Concorrente
        </Typography>
        <IconButton size="small" onClick={onClose} disabled={salvando} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {produto?.sku ? (
            <Grid item xs={12}>
              <div className={classes.cadastroRefBox}>
                <Typography
                  variant="caption"
                  className={classes.cadastroSectionCaption}
                  style={{ marginBottom: 4 }}
                >
                  Produto Karsten de referência
                </Typography>
                <Typography variant="body2" style={{ color: MP.text, lineHeight: 1.5 }}>
                  <strong>{produto.sku}</strong>
                  {produto.nome ? ` — ${produto.nome}` : ""}
                </Typography>
              </div>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Typography className={classes.cadastroSectionCaption}>
              Categoria
            </Typography>
            <div className={classes.tipoGrid}>
              <CategoriaCard
                selected={categoria === "cama"}
                title="Cama"
                icon={BedIcon}
                onSelect={() => {}}
                disabled={categoria !== "cama"}
                classes={classes}
              />
              <CategoriaCard
                selected={categoria === "banho"}
                title="Banho"
                icon={BathtubIcon}
                onSelect={() => {}}
                disabled={categoria !== "banho"}
                classes={classes}
              />
            </div>
            {submitted && errors.categoria ? (
              <Typography
                variant="body2"
                style={{ color: MP.danger, fontSize: "0.8125rem", marginTop: 6 }}
              >
                {errors.categoria}
              </Typography>
            ) : null}
          </Grid>

          {categoria ? (
            <Grid item xs={12}>
              <div className={classes.cadastroFieldsPanel}>
                <Typography className={classes.cadastroSectionCaption}>
                  Dados do concorrente
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {renderSelectField({
                      label: "Marca",
                      value: marcaId,
                      onChange: (e) => setMarcaId(e.target.value),
                      options: marcaOptions,
                      errorKey: "marca",
                      required: false,
                      emptyOptionLabel: `${MARCA_GENERICA_NOME} (padrão)`,
                    })}
                  </Grid>

                  {categoria === "cama" ? (
                    <Grid item xs={12}>
                      {renderSelectField({
                        label: "Quantidade de fios",
                        value: quantidadeFios,
                        onChange: (e) => setQuantidadeFios(e.target.value),
                        options: quantidadeFiosOptions,
                        errorKey: "quantidadeFios",
                      })}
                    </Grid>
                  ) : null}

                  {categoria === "banho" ? (
                    <Grid item xs={12}>
                      {renderSelectField({
                        label: "Gramatura",
                        value: gramatura,
                        onChange: (e) => setGramatura(e.target.value),
                        options: gramaturaOptions,
                        errorKey: "gramatura",
                      })}
                    </Grid>
                  ) : null}

                  <Grid item xs={12}>
                    <InputText
                      label="Linha concorrente"
                      value={linha}
                      onChange={(e) => setLinha(e.target.value.slice(0, LINHA_MAX))}
                      placeholder={linhaPlaceholder}
                      required
                      error={submitted && Boolean(errors.linha)}
                      props={{
                        inputProps: { maxLength: LINHA_MAX },
                      }}
                      errorMessage={
                        submitted && errors.linha
                          ? errors.linha
                          : `Nome da linha do produto concorrente (${linhaHelper})`
                      }
                    />
                  </Grid>
                </Grid>
              </div>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px", gap: 8 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={onClose}
          disabled={salvando}
          style={{ textTransform: "none", fontWeight: 600 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSalvar}
          disabled={salvando || !categoria || listasVazias}
          style={{ textTransform: "none", fontWeight: 600 }}
          startIcon={salvando ? <CircularProgress size={18} color="inherit" /> : null}
        >
          Salvar e usar na coleta
        </Button>
      </DialogActions>
    </Dialog>
  );
}

MonitoramentoPrecoCadastroConcorrenteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  produto: PropTypes.object,
  marcasConcorrentes: PropTypes.arrayOf(PropTypes.object),
  opcoesCadastroPorCategoria: PropTypes.shape({
    cama: PropTypes.object,
    banho: PropTypes.object,
  }).isRequired,
  classes: PropTypes.object.isRequired,
  onSaved: PropTypes.func.isRequired,
};

MonitoramentoPrecoCadastroConcorrenteDialog.defaultProps = {
  produto: null,
  marcasConcorrentes: [],
};
